import { getBaseUrl } from '@shared/lib/helpers/common';
import { isCallable } from '@shared/lib/helpers/common';
import { isFalsy } from '@shared/lib/helpers/common';
import { alertUnauthorized, alertUnauthenticated } from '@shared/services/base/alerts/common-alerts';

export function InterceptorWrapper(RequestClient) {
    /**
     * Request interceptors
     */
    RequestClient.interceptors.request.use(
      async config => {
        const baseURL = await getBaseUrl();
        
        // Check if this is a request to platform-api (cross-origin)
        // If the URL is a full URL to platform-api, don't override baseURL
        const requestUrl = config.url || '';
        const platformApiUrl = process.env.REACT_APP_PLATFORM_API_BASE_URL || '';
        const isPlatformApiRequest = platformApiUrl && (
          requestUrl.startsWith(platformApiUrl) || 
          requestUrl.includes('platform-api.caribe7.local')
        );
        
        // Only set baseURL if it's not a full URL to platform-api
        // This ensures axios treats it as a cross-origin request
        if (!isPlatformApiRequest) {
          config.baseURL = baseURL;
        } else {
          // For platform-api requests, ensure baseURL is not set so axios uses the full URL
          // This ensures the browser sends Origin header automatically
          config.baseURL = '';
        }
        
        // Ensure withCredentials is always true for cookie-based auth
        config.withCredentials = true;
        
        // Add Authorization header if api_token is available
        const { LocalStorage } = await import('@shared/services/main/storage');
        const api_token = LocalStorage.get('api_token');
        if (api_token) {
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${api_token}`;
        }
        
        return config;
      },
      error => Promise.reject(error)
    );

    /**
     * Response interceptors
     */
    RequestClient.interceptors.response.use(async (response) => {
        // Capture platform session_id from Set-Cookie header and store in localStorage
        // This allows us to pass it to virtual-web even if the cookie is HttpOnly
        if (response.config && response.config.url) {
          const url = response.config.url;
          const platformApiUrl = process.env.REACT_APP_PLATFORM_API_BASE_URL || '';
          const isPlatformApiRequest = platformApiUrl && (
            url.includes(platformApiUrl) || 
            url.includes('platform-api.caribe7.local')
          );
          
          if (isPlatformApiRequest) {
            // First, try to get session_id from X-Session-Id header (most reliable)
            const sessionIdHeader = response.headers['x-session-id'];
            if (sessionIdHeader) {
              const { LocalStorage } = await import('@shared/services/main/storage');
              LocalStorage.set('platform_session_id', sessionIdHeader);
              console.log('[RequestInterceptor] Stored platform_session_id from X-Session-Id header');
            } else {
              // Fallback: Try to get session_id from Set-Cookie header
              const setCookieHeader = response.headers['set-cookie'];
              if (setCookieHeader) {
                const cookies = Array.isArray(setCookieHeader) ? setCookieHeader : [setCookieHeader];
                for (const cookie of cookies) {
                  const match = cookie.match(/caribe7_session=([^;]+)/);
                  if (match && match[1]) {
                    const { LocalStorage } = await import('@shared/services/main/storage');
                    LocalStorage.set('platform_session_id', match[1]);
                    console.log('[RequestInterceptor] Stored platform_session_id from Set-Cookie header');
                    break;
                  }
                }
              }
            }
            
            // Also try to read from document.cookie after the request (in case cookie was set)
            // This is a fallback if Set-Cookie header parsing doesn't work
            // Use multiple timeouts to catch the cookie as it gets set
            [100, 300, 500].forEach(delay => {
              setTimeout(() => {
                if (typeof document !== 'undefined') {
                  const cookies = document.cookie.split(';');
                  for (let cookie of cookies) {
                    const [name, value] = cookie.trim().split('=');
                    if (name === 'caribe7_session' && value) {
                      const { LocalStorage } = require('@shared/services/main/storage');
                      const existing = LocalStorage.get('platform_session_id');
                      if (!existing || existing !== value) {
                        LocalStorage.set('platform_session_id', value);
                        console.log('[RequestInterceptor] Stored platform_session_id from document.cookie (delay:', delay + 'ms)');
                      }
                      break;
                    }
                  }
                }
              }, delay);
            });
          }
        }
        
        const {config: {service_class}} = response;
        if(response.status === 200) {
            switch (response.config.method) {
                case 'post':
                    if (isCallable(service_class, 'onCreateInterceptSuccess')) {
                        service_class['onCreateInterceptSuccess'].bind(service_class);
                        service_class['onCreateInterceptSuccess'](response);
                    }
                    break;
                case 'put':
                    if (isCallable(service_class, 'onUpdateInterceptSuccess')) {
                        service_class.onUpdateInterceptSuccess.bind(service_class);
                        service_class.onUpdateInterceptSuccess(response);
                    }
                    break;
                case 'delete':
                    if (isCallable(service_class, 'onDeleteInterceptSuccess')) {
                        service_class['onDeleteInterceptSuccess'].bind(service_class);
                        service_class['onDeleteInterceptSuccess'](response);
                    }
                    break;
                default:
                    return response;
            }
        }
        if(response.status === 401) {
            alertUnauthenticated();
        }
        if(response.status === 403) {
            alertUnauthorized();
        }
        return response;
    }, (error) => {
        const { response } = error;
        /**
         * If there is no response then this is a network error - 2023
         */
        if (response) {
            // Check if response is HTML/text instead of JSON (common for PHP error pages)
            const contentType = response.headers && response.headers['content-type'];
            const isHtmlResponse = contentType && (
                contentType.includes('text/html') || 
                contentType.includes('text/plain')
            );

            // Also check if response.data is a string (indicating HTML/text response)
            const isStringData = response.data && typeof response.data === 'string';
            
            // If HTML response detected OR data is a string (HTML), convert it to a proper error format
            if ((isHtmlResponse || isStringData) && response.data) {
                // Extract a meaningful error message or provide generic one
                const statusText = response.statusText || 'Error';
                const statusCode = response.status || 500;
                
                // Try to extract error message from HTML if possible
                let errorMessage = `Server error (${statusCode}): ${statusText}`;
                
                // If it's a 404, provide a more specific message
                if (response.status === 404) {
                    errorMessage = `Endpoint not found (404). Please check the API endpoint configuration.`;
                } else if (response.status === 500) {
                    errorMessage = `Internal server error (500). Please try again later.`;
                }

                // Replace response.data with a JSON-like structure
                response.data = {
                    message: errorMessage,
                    error: statusText,
                    status: statusCode
                };

                // Ensure response headers indicate JSON to prevent HTML rendering
                if (response.headers) {
                    response.headers['content-type'] = 'application/json';
                }
            }
            
            // Also ensure response.data is always an object, not a string
            if (response.data && typeof response.data === 'string') {
                response.data = {
                    message: response.data.length > 200 ? response.data.substring(0, 200) + '...' : response.data,
                    error: response.statusText || 'Error',
                    status: response.status || 500
                };
            }

            const {config: {service_class}} = response
            if (!isFalsy(response) && !isFalsy(response.status)) {
                if(response.status === 401) {
                    console.log('RequestClient::response:unauthenticated', response);
                    alertUnauthenticated();
                }
                if(response.status === 403) {
                    console.log('RequestClient::response:unauthorized', response);
                    
                    // Check if this is a class_disabled error
                    if (response.data && response.data.error === 'class_disabled') {
                        console.log('[ClassDisabled] Sport is disabled for this skin');
                        
                        // Show a toast notification
                        const message = response.data.message || 'This sport category is not available.';
                        
                        // Import and use alert service for notification
                        import('@shared/services/base/alerts/AlertService').then(({ default: AlertService }) => {
                            AlertService.warning(message);
                        }).catch(() => {
                            // Fallback to console if AlertService not available
                            console.warn('[ClassDisabled]', message);
                        });
                        
                        // Redirect to homepage
                        if (window.location.pathname !== '/' && window.location.pathname !== '/sports') {
                            window.location.href = '/';
                        }
                    } else {
                        alertUnauthorized();
                    }
                }
                if(response.status === 422) {
                    console.log('RequestClient::response:unprocessed', response);
                    switch (response.config.method) {
                        case 'post':
                            if (isCallable(service_class, 'onCreateInterceptFail')) {
                                service_class['onCreateInterceptFail'].bind(service_class);
                                service_class['onCreateInterceptFail'](response);
                            }
                            break;
                        case 'put':
                            if (isCallable(service_class, 'onUpdateInterceptFail')) {
                                service_class.onUpdateInterceptFail.bind(service_class);
                                service_class.onUpdateInterceptFail(response);
                            }
                            break;
                        case 'delete':
                            if (isCallable(service_class, 'onDeleteInterceptFail')) {
                                service_class['onDeleteInterceptFail'].bind(service_class);
                                service_class['onDeleteInterceptFail'](response);
                            }
                            break;
                        default:
                            return response;
                    }
                }
            }
        }
        return Promise.reject(error);
    });

    return RequestClient;
}
